// src/hooks/useSessionTime.ts
import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

export const useSessionTime = () => {
  const { user } = useAuth();
  const sessionStartRef = useRef<Date | null>(null);
  const lastActivityRef = useRef<Date>(new Date());

  useEffect(() => {
    if (!user) return;

    // Oturum başlangıcını kaydet
    sessionStartRef.current = new Date();

    // Aktivite takibi için event listener'lar
    const updateActivity = () => {
      lastActivityRef.current = new Date();
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    // Sayfa kapatılırken oturum süresini kaydet
    const handleBeforeUnload = async () => {
      if (sessionStartRef.current) {
        // Temp user ve custom auth kontrolü
        const tempSession = localStorage.getItem('temp_user_session');
        const customAuth = localStorage.getItem('custom_auth_token');
        if (tempSession || customAuth) {
          return;
        }

        const sessionDuration = Math.floor((new Date().getTime() - sessionStartRef.current.getTime()) / 1000);
        
        try {
          // Safe insert with conflict resolution
          const { error } = await supabase.rpc('safe_insert_activity_log', {
            p_user_id: user.id,
            p_action: 'session_end',
            p_details: {
              duration_seconds: sessionDuration,
              last_activity: lastActivityRef.current.toISOString()
            },
            p_ip_address: null,
            p_user_agent: navigator.userAgent
          });
          
          if (error && error.code !== '23505') {
            console.warn('Session end logging failed:', error.code);
          }
        } catch (error) {
          // Fallback to direct insert
          try {
            await supabase.from('activity_logs').insert({
              user_id: user.id,
              action: 'session_end',
              details: {
                duration_seconds: sessionDuration,
                last_activity: lastActivityRef.current.toISOString()
              },
              user_agent: navigator.userAgent
            });
          } catch (fallbackError) {
            console.warn('Session end logging failed (temp user?):', fallbackError);
          }
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Component unmount olduğunda da oturum süresini kaydet
      handleBeforeUnload();
    };
  }, [user]);
};
